/**
 * Audit Log Contracts
 * API contracts for system audit logging and activity tracking
 */

import type { AdminPaginationMeta } from './tenant.types.js'
// A type-only import: the filters are inferred from the route contract's schema, which reads this file's enums.
import type { AuditExportFilters } from './audit-log.routes.js'

// ─────────────────────────────────────────────────────────────
// ENUMS
// ─────────────────────────────────────────────────────────────

export const AuditAction = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  LOGIN: 'LOGIN',
  LOGIN_FAILED: 'LOGIN_FAILED',
  LOGOUT: 'LOGOUT',
  REGISTER: 'REGISTER',
  PASSWORD_CHANGE: 'PASSWORD_CHANGE',
  PASSWORD_RESET_REQUEST: 'PASSWORD_RESET_REQUEST',
  PASSWORD_RESET: 'PASSWORD_RESET',
  EMAIL_VERIFY: 'EMAIL_VERIFY',
  SUSPEND: 'SUSPEND',
  ACTIVATE: 'ACTIVATE',
  IMPERSONATE: 'IMPERSONATE',
  END_IMPERSONATE: 'END_IMPERSONATE',
  ROLE_CHANGE: 'ROLE_CHANGE',
  TENANT_CREATE: 'TENANT_CREATE',
  TENANT_UPDATE: 'TENANT_UPDATE',
  TENANT_SUSPEND: 'TENANT_SUSPEND',
  TENANT_ACTIVATE: 'TENANT_ACTIVATE',
  TENANT_DELETE: 'TENANT_DELETE',
  IMPORT: 'IMPORT',
  EXPORT: 'EXPORT',
  BULK_DELETE: 'BULK_DELETE',
  BULK_UPDATE: 'BULK_UPDATE',
  REPORT_GENERATE: 'REPORT_GENERATE',
  CONFIG_CHANGE: 'CONFIG_CHANGE',
  GDPR_EXPORT_REQUESTED: 'GDPR_EXPORT_REQUESTED',
  GDPR_EXPORT_REQUEUED: 'GDPR_EXPORT_REQUEUED',
  GDPR_EXPORT_COMPLETED: 'GDPR_EXPORT_COMPLETED',
  GDPR_DELETION_REQUESTED: 'GDPR_DELETION_REQUESTED',
  GDPR_DELETION_CANCELLED: 'GDPR_DELETION_CANCELLED',
  GDPR_DELETION_COMPLETED: 'GDPR_DELETION_COMPLETED',
  GDPR_DELETION_EXECUTED_BY_ADMIN: 'GDPR_DELETION_EXECUTED_BY_ADMIN',
  GDPR_DELETION_ATTEMPT_FAILED: 'GDPR_DELETION_ATTEMPT_FAILED',
  ASSIGN: 'ASSIGN',
  UNASSIGN: 'UNASSIGN',
  // Support ticket actions
  MESSAGE_ADDED: 'MESSAGE_ADDED',
  STATUS_CHANGE: 'STATUS_CHANGE',
  PRIORITY_CHANGE: 'PRIORITY_CHANGE',
  CATEGORY_CHANGE: 'CATEGORY_CHANGE',
  CUSTOMER_MESSAGE_ADDED: 'CUSTOMER_MESSAGE_ADDED',
  // Org-admin: run a data-retention policy (permanently deletes/anonymizes data)
  APPLY: 'APPLY',
  // Super-admin: replace the whole database with a backup (requested / outcome)
  RESTORE: 'RESTORE',
} as const
export type AuditAction = (typeof AuditAction)[keyof typeof AuditAction]

export const ResourceType = {
  USER: 'USER',
  TENANT: 'TENANT',
  TRANSACTION: 'TRANSACTION',
  CONFIG: 'CONFIG',
  TEMPLATE: 'TEMPLATE',
  REPORT: 'REPORT',
  IMPORT: 'IMPORT',
  DATA_SOURCE: 'DATA_SOURCE',
  SESSION: 'SESSION',
  EXCHANGE: 'EXCHANGE',
  FEATURE_FLAG: 'FEATURE_FLAG',
  TICKET: 'TICKET',
  // Org-admin configuration that changes what the organization exposes or deletes
  SSO_CONFIG: 'SSO_CONFIG',
  RETENTION_POLICY: 'RETENTION_POLICY',
  EXPORT_SCHEDULE: 'EXPORT_SCHEDULE',
  WEBHOOK: 'WEBHOOK',
  // Programmatic access: the keys that let a script or integration call the API as the organization
  API_KEY: 'API_KEY',
  // Database backups: created, downloaded, restored and deleted by a super-admin
  BACKUP: 'BACKUP',
  // Background jobs: retried or cancelled by a super-admin from the jobs monitor
  JOB: 'JOB',
  // Platform translations: drafts and publications from the super-admin «Traducciones» tab
  TRANSLATION: 'TRANSLATION',
} as const
export type ResourceType = (typeof ResourceType)[keyof typeof ResourceType]

/**
 * ⚠️⚠️ LOS RECURSOS QUE ALGUIEN ESCRIBE DE VERDAD EN `bat.audit_log`.
 *
 * `ResourceType` es el vocabulario CERRADO del contrato: la API sigue
 * aceptándolo entero, porque un cliente puede preguntar por lo que quiera.
 * Esta lista es otra cosa — lo que el DESPLEGABLE del filtro debe ofrecer—, y
 * existe porque ofrecer un recurso que nadie registra convierte una lista
 * vacía en una respuesta falsa.
 *
 * MEDIDO el 2026-09-21 sobre `bat_e2e` y sobre todo el backend:
 *
 *   recurso        escritores en código   filas en la tabla
 *   USER                   11                   3360
 *   TENANT                  8                     45
 *   TRANSACTION             9                  13839
 *   REPORT                  4                   1331
 *   IMPORT                  3                    105
 *   DATA_SOURCE             9                    705
 *   SESSION                 4                  13920
 *   TICKET                  7                    191
 *   ── y los cuatro que NADIE escribía ──
 *   CONFIG                  0                      0
 *   TEMPLATE                0                      0
 *   EXCHANGE                0                      0
 *   FEATURE_FLAG            0                      0
 *
 * Aquella vez la corrección fue dejar de ofrecerlos. El 2026-09-23 se cableó
 * lo que faltaba: el panel de auditoría no contaba quién creó, renombró o
 * borró un exchange, editó o restauró una plantilla de importación ni tocó un
 * catálogo — y esos cambios reclasifican las importaciones de TODOS los
 * clientes. Hoy los escribe `platform/audit/audit-route-map.ts` (sección
 * «CATALOGUE THAT SHAPES EVERY CUSTOMER'S BOOKS»), así que se ofrecen.
 *
 * Lo sujeta `audited-resource-types-have-a-writer.guard.test.ts`: un recurso
 * ofrecido sin escritor, o uno excluido que gana escritor, lo pone en rojo.
 */
export const AUDITED_RESOURCE_TYPES = [
  ResourceType.USER,
  ResourceType.TENANT,
  ResourceType.TRANSACTION,
  ResourceType.REPORT,
  ResourceType.IMPORT,
  ResourceType.DATA_SOURCE,
  ResourceType.SESSION,
  ResourceType.TICKET,
  ResourceType.EXCHANGE,
  ResourceType.TEMPLATE,
  ResourceType.CONFIG,
  ResourceType.SSO_CONFIG,
  ResourceType.RETENTION_POLICY,
  ResourceType.EXPORT_SCHEDULE,
  ResourceType.WEBHOOK,
  ResourceType.API_KEY,
  ResourceType.BACKUP,
  ResourceType.JOB,
  ResourceType.TRANSLATION,
] as const satisfies readonly ResourceType[]

/** Del contrato y sin escritor hoy: FEATURE_FLAG (sus rutas se retiraron con #2901). Sigue en ResourceType y con etiqueta para que sus filas se lean; el filtro no lo ofrece. */
export const UNWRITTEN_RESOURCE_TYPES = [
  ResourceType.FEATURE_FLAG,
] as const satisfies readonly ResourceType[]

// ─────────────────────────────────────────────────────────────
// ORG-ADMIN AUDIT VOCABULARY (feature-specific, currently lowercase)
//
// AuditAction / ResourceType above = the CLOSED super-admin vocabulary
// surfaced in the super-admin audit-log filter dropdown and validators
// (Object.values / z.nativeEnum). Keep that set free of org-only members so
// those UIs/validators stay clean.
//
// OrgAuditAction / OrgResourceType below = the (currently lowercase,
// feature-specific) vocabulary WRITTEN by org-admin services (org-users,
// org-roles, org-teams, org-settings, tenant-management, permissions-matrix).
// They are kept separate so the super-admin surfaces are not polluted with
// case-duplicates (CREATE vs create) or org-only actions.
//
// NOTE: Reconciling the casing/overlap between the two vocabularies (e.g.
// normalizing 'create' → 'CREATE' in stored rows) is a separate
// data-normalization task and is intentionally NOT done here.
// ─────────────────────────────────────────────────────────────

export const OrgAuditAction = {
  // Org admin lowercase actions (org-users / org-roles / org-teams / org-settings services)
  CREATE_LOWER: 'create',
  UPDATE_LOWER: 'update',
  DELETE_LOWER: 'delete',
  IMPORT_LOWER: 'import',
  SUSPEND_LOWER: 'suspend',
  ACTIVATE_LOWER: 'activate',
  RESEND_INVITE: 'resend_invite',
  CANCEL_INVITATION: 'cancel_invitation',
  REVOKE_SESSION: 'revoke_session',
  REVOKE_ALL_SESSIONS: 'revoke_all_sessions',
  RESET_2FA: 'reset_2fa',
  RESET_PASSWORD: 'reset_password',
  ASSIGN_PERMISSIONS: 'assign_permissions',
  REPLACE_PERMISSIONS: 'replace_permissions',
  REMOVE_PERMISSION: 'remove_permission',
  ASSIGN_ROLE: 'assign_role',
  REMOVE_ROLE: 'remove_role',
  GRANT_PERMISSIONS: 'grant_permissions',
  REVOKE_PERMISSION: 'revoke_permission',
  BULK_GRANT_PERMISSIONS: 'bulk_grant_permissions',
  BULK_REVOKE_PERMISSIONS: 'bulk_revoke_permissions',
  ADD_MEMBERS: 'add_members',
  REMOVE_MEMBER: 'remove_member',
  UPDATE_MEMBER_ROLE: 'update_member_role',
  TRANSFER_OWNERSHIP: 'transfer_ownership',
  // Permissions matrix
  PERMISSION_UPDATE: 'PERMISSION_UPDATE',
} as const
export type OrgAuditAction = (typeof OrgAuditAction)[keyof typeof OrgAuditAction]

export const OrgResourceType = {
  // Org admin lowercase resource types (org-users / org-roles / org-teams / org-settings services)
  USER_LOWER: 'user',
  TENANT_LOWER: 'tenant',
  SESSION_LOWER: 'session',
  CUSTOM_ROLE: 'custom_role',
  TEAM: 'team',
  INTEGRATION: 'integration',
  SETTINGS: 'settings',
  NOTIFICATION_SETTINGS: 'notification_settings',
  SECURITY_SETTINGS: 'security_settings',
  BRANDING_SETTINGS: 'branding_settings',
  BRANDING_LOGO: 'branding_logo',
  MFA_SETTINGS: 'mfa_settings',
  // Permissions matrix
  ROLE_LOWER: 'role',
} as const
export type OrgResourceType = (typeof OrgResourceType)[keyof typeof OrgResourceType]

// ─────────────────────────────────────────────────────────────
// RESPONSE DTOs
// ─────────────────────────────────────────────────────────────

export interface AuditLogEntryResponse {
  id: string
  timestamp: string
  actor: {
    id: string
    email: string
    name: string
  }
  action: AuditAction
  resourceType: ResourceType
  resourceId: string | null
  resourceName: string | null
  changes: {
    old: Record<string, unknown> | null
    new: Record<string, unknown> | null
  }
  metadata: {
    ipAddress: string | null
    userAgent: string | null
    /**
     * The super-admin who made the change while impersonating `actor`, or
     * null. Without it an admin's change and the customer's own read the same.
     */
    impersonatedBy?: string | null
  }
}

export interface AuditLogListResponse {
  items: AuditLogEntryResponse[]
  pagination: AdminPaginationMeta
}

// ─────────────────────────────────────────────────────────────
// AUDIT LOG EXPORT - Async export functionality
// ─────────────────────────────────────────────────────────────

export const AuditExportFormat = {
  CSV: 'csv',
  JSON: 'json',
  PDF: 'pdf',
} as const
export type AuditExportFormat = (typeof AuditExportFormat)[keyof typeof AuditExportFormat]

export const AuditExportStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  EXPIRED: 'expired',
} as const
export type AuditExportStatus = (typeof AuditExportStatus)[keyof typeof AuditExportStatus]

export interface RequestAuditExportRequest {
  format: AuditExportFormat
  filters: AuditExportFilters
  emailRecipient?: string
}
